// import { ReactNodeViewRenderer } from "@tiptap/react";
import { Mark } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import cn from "classnames";
import { v4 as uuid } from "uuid";

import { getSourceDocumentAttrs, getArticleLinkAttrs } from "./helpers/pasteHandler";
import { ReportDocumentSection } from "@app/constants";
import { getBookmarkLink } from "@app/components/document/bookmark/utils";
import notify from "@app/components/notify";

import "./style.scss";

const CaptisLinkMark = Mark.create({
    name: ReportDocumentSection.CAPTIS_LINK.MARK_NAME,
    inline: true,
    inclusive: false,
    inclusiveRight: false,

    onBeforeCreate() {
        this.storage.store = this.options.store || {};
    },

    addAttributes() {
        return {
            area: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-area"),
                renderHTML: (attrs) => ({ "data-area": attrs.area }),
            },
            excel: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-excel"),
                renderHTML: (attrs) => ({ "data-excel": attrs.excel }),
            },
            documentId: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-documentId"),
                renderHTML: (attrs) => ({ "data-documentId": attrs.documentId }),
            },
            id: {
                default: uuid(),
                parseHTML: (element) => element.getAttribute("id"),
                renderHTML: false,
            },
            project: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-project"),
                renderHTML: (attrs) => ({ "data-project": attrs.project }),
            },
            articleId: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-articleid"),
                renderHTML: (attrs) =>
                    attrs.articleId ? { "data-articleid": attrs.articleId } : {},
            },
            stage: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-stage"),
                renderHTML: (attrs) => (attrs.stage ? { "data-stage": attrs.stage } : {}),
            },
            articleType: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-articletype"),
                renderHTML: (attrs) =>
                    attrs.articleType ? { "data-articletype": attrs.articleType } : {},
            },
            section: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-section"),
                renderHTML: (attrs) => ({ "data-section": attrs.section }),
            },
            type: {
                default: null,
            },
            target: {
                default: "_blank",
            },
            class: {
                default: undefined,
                renderHTML: (attrs) => {
                    const { isFocused } = attrs;

                    return {
                        class: cn({
                            "captis-link": true,
                            focused: isFocused,
                        }),
                    };
                },
            },
        };
    },

    addStorage() {
        return {
            store: {},
        };
    },

    parseHTML() {
        return [
            {
                tag: `span.${ReportDocumentSection.CAPTIS_LINK.MARK_NAME}`,
                getAttrs: (node) => {
                    const newId = uuid();
                    return {
                        id: newId,
                        area: node.getAttribute("data-area"),
                        excel: node.getAttribute("data-excel"),
                        documentId: node.getAttribute("data-documentId"),
                        project: node.getAttribute("data-project"),
                        section: node.getAttribute("data-section"),
                        type: node.getAttribute("type"),
                        target: node.getAttribute("target"),
                        articleId: node.getAttribute("data-articleid"),
                        stage: node.getAttribute("data-stage"),
                        articleType: node.getAttribute("data-articletype"),
                    };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        // Remove the id attribute from HTML serialization to prevent conflicts on paste
        const { id, ...attributesWithoutId } = HTMLAttributes;
        return ["span", attributesWithoutId, 0];
    },

    addCommands() {
        return {
            insertCaptisLink: (link) => ({ editor, tr, commands }) => {
                if (link) {
                    if (link.project !== editor.options.editorProps.projectId) {
                        notify.info("Cannot insert link! The link is not from this project.");
                        return;
                    }

                    const { sectionId } = editor;
                    const { selection } = editor.state;
                    const { from, to } = selection;

                    // prepare the node attrs
                    const attrs = { ...link, section: sectionId, id: uuid() };
                    delete attrs.text;

                    const markType =
                        editor.schema.marks[ReportDocumentSection.CAPTIS_LINK.MARK_NAME];
                    const mark = markType.create(attrs);

                    if (link.text) {
                        const textNode = editor.schema.text(link.text, [mark]);

                        // fix for mismatched transaction error
                        setTimeout(() => {
                            editor
                                .chain()
                                .insertContentAt(from, textNode.toJSON(), {
                                    updateSelection: true,
                                    parseOptions: {
                                        preserveWhitespace: "full",
                                    },
                                })
                                .focus()
                                .setTextSelection(from, from + link.text.length)
                                .setMark(ReportDocumentSection.CAPTIS_LINK.MARK_NAME, attrs)
                                .run();
                        }, 0);
                    } else {
                        const tr = editor.state.tr;

                        tr.addMark(from, to, mark);

                        // fix for mismatched transaction error
                        setTimeout(() => {
                            editor.view.dispatch(tr);
                        }, 0);
                    }
                }
            },

            toggleCaptisLink: () => async ({ editor, commands, tr }) => {
                const { selection, doc } = editor.state;
                const selectedText = doc.textBetween(selection.from, selection.to);
                if (selectedText) {
                    const markType =
                        editor.schema.marks[ReportDocumentSection.CAPTIS_LINK.MARK_NAME];
                    const markPresent = doc.rangeHasMark(selection.from, selection.to, markType);
                    if (markPresent) {
                        setTimeout(() => {
                            editor
                                .chain()
                                .unsetMark(ReportDocumentSection.CAPTIS_LINK.MARK_NAME)
                                .run();
                        }, 0);
                    } else {
                        try {
                            let tempLink, tempType;
                            const clipboardData = await navigator.clipboard.read();
                            const clipboardItem = clipboardData?.[0];

                            if (clipboardItem?.types) {
                                await Promise.all(
                                    clipboardItem.types.map(async (type) => {
                                        if ("text/html" === type) {
                                            let html = await clipboardItem.getType("text/html");
                                            if (html) {
                                                const htmlText = await html.text();
                                                const articleAttrs = getArticleLinkAttrs(htmlText);
                                                if (articleAttrs) {
                                                    tempLink = articleAttrs;
                                                    tempType =
                                                        ReportDocumentSection.CAPTIS_LINK.TYPE
                                                            .ARTICLE;
                                                } else {
                                                    const sourceDocAttrs = getSourceDocumentAttrs(
                                                        htmlText,
                                                    );
                                                    if (sourceDocAttrs) {
                                                        const href = editor.commands.buildSourceDocumentHref(
                                                            sourceDocAttrs,
                                                        );
                                                        tempLink = editor.commands.extractSourceDocumentLink(
                                                            href,
                                                        );
                                                        tempType =
                                                            ReportDocumentSection.CAPTIS_LINK.TYPE
                                                                .SOURCE_DOCUMENT;
                                                    }
                                                }
                                            }
                                        }
                                    }),
                                );
                            }

                            if (!tempLink) {
                                notify.info(
                                    "No source document link found! Please use the copy feature from source document",
                                );
                            } else {
                                editor
                                    .chain()
                                    .focus()
                                    .insertCaptisLink({ ...tempLink, type: tempType })
                                    .run();
                            }
                        } catch (error) {
                            notify.error("Failed to access clipboard. Please try again.");
                        }
                    }
                }
            },

            buildArticleHref: ({ project, articleType, stage, articleId, area }) => () => {
                const params = new URLSearchParams({
                    "view-file": "true",
                    highlight: "true",
                    focus: "true",
                });
                if (area) {
                    params.set("area", area);
                }
                return `${
                    window.location.origin
                }/r/${project}/${articleType}/${stage}/${articleId}?${params.toString()}`;
            },

            buildSourceDocumentHref: ({ project, documentId, area, excel }) => () => {
                const bookmark = {
                    document: documentId,
                };

                if (excel) {
                    try {
                        bookmark.excel = JSON.parse(excel);
                        // eslint-disable-next-line no-empty
                    } catch (ex) {}
                } else if (area) {
                    try {
                        bookmark.areas = [JSON.parse(area)];
                        // eslint-disable-next-line no-empty
                    } catch (ex) {}
                }

                const url =
                    window.location.origin +
                    getBookmarkLink({ bookmark, baseUrl: `/fp/r/${project}` });

                return url;
            },

            extractSourceDocumentLink: (href) => () => {
                const url = new URL(href);
                const area = url.searchParams.get("area");
                const excel = url.searchParams.get("excel");

                const parts = url.pathname.split("/");
                const project = parts[3];
                const documentId = parts[6];

                return { project, documentId, area, excel };
            },
        };
    },

    addProseMirrorPlugins() {
        const markName = ReportDocumentSection.CAPTIS_LINK.MARK_NAME;

        return [
            new Plugin({
                key: new PluginKey("captisLinkNormalization"),
                appendTransaction: (transactions, _oldState, newState) => {
                    if (!transactions.some((tr) => tr.docChanged)) {
                        return null;
                    }
                    if (transactions.some((tr) => tr.getMeta("captisLinkNormalization"))) {
                        return null;
                    }

                    const { tr, schema } = newState;
                    const markType = schema.marks[markName];
                    if (!markType) {
                        return null;
                    }

                    const parentIds = new WeakMap();
                    let parentCounter = 0;
                    const groupToCanonical = {};
                    const toNormalize = [];

                    newState.doc.descendants((node, pos, parent) => {
                        if (!node.isText) {
                            return;
                        }

                        const captisLinkMark = node.marks.find((m) => m.type === markType);
                        if (!captisLinkMark) {
                            return;
                        }

                        const { documentId, articleId, area, project, id } = captisLinkMark.attrs;

                        if (!parentIds.has(parent)) {
                            parentIds.set(parent, ++parentCounter);
                        }
                        const pid = parentIds.get(parent);
                        const referenceId = documentId || articleId;
                        const groupKey = `${pid}:${referenceId}:${area}:${project}`;

                        if (!groupToCanonical[groupKey]) {
                            groupToCanonical[groupKey] = id || uuid();
                        }

                        const canonicalId = groupToCanonical[groupKey];
                        if (id !== canonicalId) {
                            toNormalize.push({
                                from: pos,
                                to: pos + node.nodeSize,
                                currentMark: captisLinkMark,
                                canonicalMark: markType.create({
                                    ...captisLinkMark.attrs,
                                    id: canonicalId,
                                }),
                            });
                        }
                    });

                    if (toNormalize.length === 0) {
                        return null;
                    }

                    tr.setMeta("captisLinkNormalization", true);
                    for (const { from, to, currentMark, canonicalMark } of toNormalize) {
                        tr.removeMark(from, to, currentMark);
                        tr.addMark(from, to, canonicalMark);
                    }

                    return tr;
                },
            }),
        ];
    },
});

export default CaptisLinkMark;
